import React, { useState, useEffect, useRef } from 'react';
import ReactConfetti from 'react-confetti';
import Player from './Player';
import Deck from './Deck';
import {
  createDeck,
  shuffleDeck,
  dealCards,
  isValidPlay, 
  getNextPlayer,
  initializePlayers,
  handleSpecialCard
} from '../utils/gameUtils';
import '../styles/Game.css';

// Game component

const Game = () => {
  const [showHelp, setShowHelp] = useState(false);
  const [unoCountdown, setUnoCountdown] = useState(-1);
  const unoIntervalRef = useRef(null);
  
  // Create initial setup for visual display before game starts
  const createInitialSetup = () => {
    // Create a deck
    let deck = createDeck();
    deck = shuffleDeck(deck);
    
    // Deal cards to players (1 human, 3 computer)
    const { hands, deck: remainingDeck } = dealCards(deck, 4);
    
    // Initialize players with their hands
    const players = initializePlayers(hands);
    
    // Take a card from the deck for the discard pile
    const startingCard = remainingDeck[0]; // Just for display, not actually removed
    
    return {
      players,
      deck: remainingDeck,
      discardPile: [startingCard]
    };
  };
  
  // Initialize with visual setup
  const initialSetup = createInitialSetup();
  
  const [gameState, setGameState] = useState({
    players: initialSetup.players,
    currentPlayer: 0,
    direction: 1, // 1 for clockwise, -1 for counter-clockwise
    deck: initialSetup.deck,
    discardPile: initialSetup.discardPile,
    currentColor: initialSetup.discardPile[0].color,
    gameStarted: false,
    gameOver: false,
    winner: null,
    message: 'Welcome to UNO! Click Start Game to begin.',
    showColorPicker: false,
    pendingWildCard: null,
    unoButtonPressed: false,
    needToCallUno: false
  });

  // Initialize game
  const initializeGame = () => {
    // Create and shuffle a new deck
    let deck = createDeck();
    deck = shuffleDeck(deck);
    
    // Deal cards to players (1 human, 3 computer)
    const { hands, deck: remainingDeck } = dealCards(deck, 4);
    
    // Initialize players with their hands
    const players = initializePlayers(hands);
    
    // Take the top card from the deck to start the discard pile
    // Make sure it's not a wild card
    let startingCard;
    let newDeck = [...remainingDeck];
    
    do {
      startingCard = newDeck.pop();
      // If it's a wild card, put it back in the deck and shuffle again
      if (startingCard.type === 'wild' || startingCard.type === 'wild-draw-four') {
        newDeck.unshift(startingCard);
        newDeck = shuffleDeck(newDeck);
      }
    } while (startingCard.type === 'wild' || startingCard.type === 'wild-draw-four');
    
    setGameState({
      players,
      currentPlayer: 0, // Human player starts
      direction: 1,
      deck: newDeck,
      discardPile: [startingCard],
      currentColor: startingCard.color,
      gameStarted: true,
      gameOver: false,
      winner: null,
      message: 'Game started! Your turn.',
      showColorPicker: false,
      pendingWildCard: null,
      unoButtonPressed: false,
      needToCallUno: false
    });
  };

  // Start a new game
  const startGame = () => {
    initializeGame();
  };

  // Handle card play
  const playCard = (card, selectedColor = null) => {
    console.log('Playing card:', card, 'with color:', selectedColor);
    setGameState(prevState => {
      // Get the current player
      const currentPlayerIndex = prevState.currentPlayer;
      const currentPlayer = {...prevState.players[currentPlayerIndex]};
      
      // Check if the card is valid to play
      const topCard = prevState.discardPile[prevState.discardPile.length - 1];
      const isValid = isValidPlay(card, topCard, prevState.currentColor);
      
      if (!isValid) {
        return {
          ...prevState,
          message: 'Invalid move! Try another card.'
        };
      }
      
      // Handle wild cards that need color selection
      if ((card.type === 'wild' || card.type === 'wild-draw-four') && !selectedColor) {
        return {
          ...prevState,
          showColorPicker: true,
          pendingWildCard: card,
          message: 'Select a color for your wild card.'
        };
      }
      
      // Remove the card from the player's hand
      const cardIndex = currentPlayer.hand.findIndex(
        c => c.color === card.color && c.value === card.value && c.type === card.type
      );
      
      if (cardIndex === -1) {
        return prevState; // Card not found in hand
      }
      
      currentPlayer.hand.splice(cardIndex, 1);
      
      // Check if the player has won
      if (currentPlayer.hand.length === 0) {
        return {
          ...prevState,
          players: [
            ...prevState.players.slice(0, currentPlayerIndex),
            currentPlayer,
            ...prevState.players.slice(currentPlayerIndex + 1)
          ],
          discardPile: [...prevState.discardPile, card],
          gameOver: true,
          winner: currentPlayer.name,
          message: `${currentPlayer.name} wins!`
        };
      }
      
      // Handle special card effects
      const { nextPlayer, newDirection, drawCards, affectedPlayer } = handleSpecialCard(
        card,
        {
          currentPlayer: currentPlayerIndex,
          direction: prevState.direction,
          players: prevState.players
        }
      );
      
      // Update the next player's hand if they need to draw cards
      let updatedPlayers = [...prevState.players];
      updatedPlayers[currentPlayerIndex] = currentPlayer;
      
      // Create a copy of the deck that we'll modify
      let updatedDeck = [...prevState.deck];
      
      if (drawCards > 0) {
        // Use affectedPlayer (the player immediately after current) for Draw Two/Four cards
        const playerToDraw = affectedPlayer;
        const playerObj = {...updatedPlayers[playerToDraw]};
        
        console.log(`Player ${currentPlayerIndex} played ${card.type}, ${playerToDraw} draws ${drawCards} cards`);
        
        // Draw cards for the affected player
        for (let i = 0; i < drawCards && updatedDeck.length > 0; i++) {
          playerObj.hand.push(updatedDeck.pop());
        }
        
        updatedPlayers[playerToDraw] = playerObj;
      }
      
      
      // Determine the new current color
      const newColor = selectedColor || card.color || prevState.currentColor;
      
      // Make sure we're using the updated deck if cards were drawn
      let finalDeck = prevState.deck;
      if (drawCards > 0) {
        finalDeck = [...updatedDeck];
      }

      return {
        ...prevState,
        players: updatedPlayers,
        currentPlayer: nextPlayer,
        direction: newDirection,
        discardPile: [...prevState.discardPile, card],
        deck: finalDeck,
        currentColor: newColor,
        message: `${currentPlayer.name} played ${card.color || ''} ${card.value}.`,
        showColorPicker: false,
        pendingWildCard: null,
        unoButtonPressed: false,
        needToCallUno: false
      };
    });
  };

  // Handle drawing a card
  const drawCard = () => {
    setGameState(prevState => {
      // Get the current player
      const currentPlayerIndex = prevState.currentPlayer;
      const currentPlayer = {...prevState.players[currentPlayerIndex]};
      
      // Check if the current player is human and has playable cards
      if (!currentPlayer.isComputer) {
        const topCard = prevState.discardPile[prevState.discardPile.length - 1];
        const hasPlayableCard = currentPlayer.hand.some(card => 
          isValidPlay(card, topCard, prevState.currentColor)
        );
        
        if (hasPlayableCard) {
          return {
            ...prevState,
            message: 'You have a playable card! You cannot draw.'
          };
        }
      }
      
      // Check if the deck is empty
      if (prevState.deck.length === 0) {
        // If the discard pile has only one card, we can't reshuffle
        if (prevState.discardPile.length <= 1) {
          return {
            ...prevState,
            message: 'No cards left to draw!'
          };
        }
        
        // Take the top card from the discard pile
        const topCard = prevState.discardPile[prevState.discardPile.length - 1];
        
        // Shuffle the rest of the discard pile to create a new deck
        const newDeck = shuffleDeck(prevState.discardPile.slice(0, -1));
        
        return {
          ...prevState,
          deck: newDeck,
          discardPile: [topCard],
          message: 'Deck reshuffled.'
        };
      }
      
      // Draw a card from the deck
      const newDeck = [...prevState.deck];
      const drawnCard = newDeck.pop();
      
      // Add the card to the player's hand
      currentPlayer.hand.push(drawnCard);
      
      // Update the players array
      const updatedPlayers = [
        ...prevState.players.slice(0, currentPlayerIndex),
        currentPlayer,
        ...prevState.players.slice(currentPlayerIndex + 1)
      ];
      
      // Move to the next player
      const nextPlayer = getNextPlayer(
        currentPlayerIndex,
        prevState.direction,
        prevState.players.length
      );
      
      return {
        ...prevState,
        players: updatedPlayers,
        deck: newDeck,
        currentPlayer: nextPlayer,
        message: `${currentPlayer.name} drew a card.`,
        unoButtonPressed: false
      };
    });
  };

  // Handle color selection for wild cards
  const handleColorSelection = (color) => {
    if (gameState.pendingWildCard) {
      playCard(gameState.pendingWildCard, color);
    }
  };

  // Start the UNO countdown (called after human plays down to 1 card)
  const startUnoCountdown = () => {
    clearUnoCountdown();
    setUnoCountdown(10);
    let remaining = 10;
    unoIntervalRef.current = setInterval(() => {
      remaining -= 1;
      setUnoCountdown(remaining);
      if (remaining <= 0) {
        clearUnoCountdown();
        // Penalty: draw 2 cards
        setGameState(prevState => {
          const newDeck = [...prevState.deck];
          const updatedPlayers = [...prevState.players];
          const human = { ...updatedPlayers[0], hand: [...updatedPlayers[0].hand] };
          for (let i = 0; i < 2 && newDeck.length > 0; i++) {
            human.hand.push(newDeck.pop());
          }
          updatedPlayers[0] = human;
          return { ...prevState, players: updatedPlayers, deck: newDeck, message: 'Too slow! You drew 2 cards.' };
        });
      }
    }, 1000);
  };

  const clearUnoCountdown = () => {
    clearInterval(unoIntervalRef.current);
    setUnoCountdown(-1);
  };

  // Handle UNO button press
  const handleUnoButtonPress = () => {
    clearUnoCountdown();
    setGameState(prevState => ({
      ...prevState,
      unoButtonPressed: true,
      message: 'UNO!'
    }));
  };

  // Get window dimensions for confetti
  const windowSize = useRef({ width: window.innerWidth, height: window.innerHeight });
  
  // Update window dimensions when the window is resized
  useEffect(() => {
    const handleResize = () => {
      windowSize.current = { width: window.innerWidth, height: window.innerHeight };
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Trigger UNO countdown when human player reaches 1 card
  const humanHandLength = gameState.players[0]?.hand.length;
  useEffect(() => {
    if (!gameState.gameStarted || gameState.gameOver) return;
    if (humanHandLength === 1) {
      startUnoCountdown();
    } else {
      clearUnoCountdown();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [humanHandLength, gameState.gameStarted, gameState.gameOver]);

  // Always keep a ref to the latest gameState to avoid stale closures
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;

  // Handle computer turns - fires only when currentPlayer changes
  useEffect(() => {
    if (!gameState.gameStarted || gameState.gameOver) return;

    const currentPlayerIndex = gameState.currentPlayer;
    const currentPlayer = gameState.players[currentPlayerIndex];

    if (!currentPlayer || !currentPlayer.isComputer) return;

    // Schedule the computer's move. We do NOT cancel this in cleanup
    // because React cleanup runs on every dependency change and would
    // cancel the timeout before it fires.
    const timeoutId = setTimeout(() => {
      const s = gameStateRef.current;

      // Bail out if the turn has already moved on
      if (!s.gameStarted || s.gameOver) return;
      if (s.currentPlayer !== currentPlayerIndex) return;
      if (!s.players[currentPlayerIndex].isComputer) return;

      const player = s.players[currentPlayerIndex];
      const topCard = s.discardPile[s.discardPile.length - 1];
      const playableCards = player.hand.filter(card =>
        isValidPlay(card, topCard, s.currentColor)
      );

      if (playableCards.length > 0) {
        const cardToPlay = playableCards[0];
        if (cardToPlay.type === 'wild' || cardToPlay.type === 'wild-draw-four') {
          const colors = { red: 0, blue: 0, green: 0, yellow: 0 };
          player.hand.forEach(c => { if (c.color) colors[c.color]++; });
          let bestColor = 'red';
          let maxCount = 0;
          Object.entries(colors).forEach(([color, count]) => {
            if (count > maxCount) { maxCount = count; bestColor = color; }
          });
          playCard(cardToPlay, bestColor);
        } else {
          playCard(cardToPlay);
        }
      } else {
        drawCard();
      }
    }, 2000);

    // Only cancel if the game ends or the component unmounts
    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.currentPlayer, gameState.gameStarted, gameState.gameOver]);

  return (
    <div className="game-container">
      {/* Top-right help button */}
      <button
        className="help-button"
        onClick={() => setShowHelp(true)}
        title="How to play"
      >
        ?
      </button>

      {/* How to play modal */}
      {showHelp && (
        <div className="help-overlay" onClick={() => setShowHelp(false)}>
          <div className="help-modal" onClick={e => e.stopPropagation()}>
            <button className="help-close" onClick={() => setShowHelp(false)}>✕</button>
            <h2>How to Play UNO</h2>
            <ul>
              <li><strong>Objective:</strong> Be the first player to get rid of all your cards.</li>
              <li><strong>On your turn:</strong> Play a card matching the top discard card by color, number, or symbol. If you have no playable card, click the draw pile to draw one.</li>
              <li><strong>Skip:</strong> The next player loses their turn.</li>
              <li><strong>Reverse:</strong> Reverses the direction of play. The direction indicator below the deck will update.</li>
              <li><strong>Draw Two:</strong> The next player draws 2 cards and loses their turn.</li>
              <li><strong>Wild:</strong> Play on any card and choose the color to continue play.</li>
              <li><strong>Wild Draw Four:</strong> Choose a color and the next player draws 4 cards and loses their turn.</li>
              <li><strong>UNO:</strong> When you play down to 1 card, a red UNO button will appear with a 10-second countdown. Click it in time or draw 2 penalty cards!</li>
              <li><strong>Whose turn:</strong> The active player's name turns red and bold.</li>
              <li><strong>Winning:</strong> Play your last card to win the game!</li>
            </ul>
            <button className="help-close-bottom" onClick={() => setShowHelp(false)}>Got it!</button>
          </div>
        </div>
      )}
      
      {/* Players positioned directly in the game container */}
      {gameState.players.map((player, index) => (
        <Player 
          key={index}
          player={player}
          isCurrentPlayer={index === gameState.currentPlayer}
          onCardPlay={index === 0 && gameState.gameStarted && !gameState.gameOver ? playCard : null}
          position={index}
          gameStarted={gameState.gameStarted}
        />
      ))}
      
      {/* UNO countdown button - appears above human player when they have 1 card */}
      {unoCountdown >= 0 && (
        <div className="uno-countdown-container">
          <button className="uno-countdown-button" onClick={handleUnoButtonPress}>
            UNO! <span className="uno-timer">{unoCountdown}s</span>
          </button>
        </div>
      )}

      {gameState.gameStarted && gameState.showColorPicker && (
        <div className="color-picker-overlay">
          <div className="color-picker-modal">
            <p>Choose a color</p>
            <div className="color-picker-options">
              <div className="color-option red" onClick={() => handleColorSelection('red')}></div>
              <div className="color-option blue" onClick={() => handleColorSelection('blue')}></div>
              <div className="color-option green" onClick={() => handleColorSelection('green')}></div>
              <div className="color-option yellow" onClick={() => handleColorSelection('yellow')}></div>
            </div>
          </div>
        </div>
      )}
      
      <div className="game-board">
        {!gameState.gameStarted ? (
          <button className="start-button" onClick={startGame}>Start Game</button>
        ) : (
          <Deck 
            deck={gameState.deck} 
            discardPile={gameState.discardPile}
            onDrawCard={gameState.currentPlayer === 0 && !gameState.gameOver ? drawCard : null}
            currentColor={gameState.currentColor}
            direction={gameState.direction}
          />
        )}
      </div>
      
      {/* Computer player logic is now handled directly in Game component */}
      
      {gameState.gameOver && (
        <>
          <ReactConfetti
            width={windowSize.current.width}
            height={windowSize.current.height}
            recycle={true}
            numberOfPieces={200}
            gravity={0.1}
            colors={['#D50000', '#2196F3', '#4CAF50', '#FFEB3B']}
          />
          <div className="game-over">
            <h2>Game Over!</h2>
            <p>Winner: {gameState.winner}</p>
            <button onClick={initializeGame}>Play Again</button>
          </div>
        </>
      )}
    </div>
  );
};

export default Game;
